<template>
  <div class="admin-dashboard-container fade-in">
    <!-- Live Alerts Box (Tac-tile feedback banner) -->
    <div v-if="alertVisible" class="live-alert-banner shadow-sm mb-4">
      <div class="alert-left">
        <span class="pulse-dot"></span>
        <icon icon="fa-solid fa-bell" class="alert-icon" />
        <span class="alert-text font-serif"><strong>Hệ thống:</strong> Có <strong>5 cuốn sách</strong> đã quá hạn trả. Vui lòng kiểm tra danh sách mượn trả.</span>
      </div>
      <button @click="dismissAlert" class="btn-dismiss-alert" aria-label="Dismiss Alert">
        <icon icon="fa-solid fa-xmark" />
      </button>
    </div>

    <!-- Segmented Control Tab Switcher (iOS style) -->
    <div class="segmented-control-tabs mb-4">
      <button 
        v-for="tab in tabs" 
        :key="tab.id"
        @click="activeTab = tab.id"
        :class="['tab-btn font-serif', { 'active': activeTab === tab.id }]"
      >
        <icon :icon="tab.icon" class="me-2 d-none d-lg-inline" />
        <span class="d-none d-md-inline">{{ tab.label }}</span>
        <span class="d-inline d-md-none">{{ tab.shortLabel }}</span>
      </button>
      <div class="slider-tab" :style="sliderStyle"></div>
    </div>

    <!-- ==============================================
         TAB 1: TỔNG QUAN (METRICS & RECENT ACTIONS)
         ============================================== -->
    <div v-if="activeTab === 'overview'" class="tab-pane-content fade-in">
      <!-- Quick Statistics Cards Bento Grid (Double-Bezel Architecture) -->
      <section class="metrics-grid row g-4 mb-5">
        <!-- Total Books Metric -->
        <div class="col-12 col-sm-6 col-xl-3">
          <div class="metric-card-shell">
            <div class="metric-card-core">
              <div class="metric-card-body">
                <div class="metric-details">
                  <span class="metric-label font-serif">Tổng số Sách</span>
                  <h3 class="metric-value">{{ stats.totalBooks }}</h3>
                </div>
                <div class="metric-icon-box metric-icon-primary">
                  <icon icon="fa-solid fa-book" />
                </div>
              </div>
              <div class="metric-footer">
                <span class="metric-trend text-success"><icon icon="fa-solid fa-arrow-trend-up" /> +12%</span>
                <span class="metric-trend-label">so với tháng trước</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Total Users Metric -->
        <div class="col-12 col-sm-6 col-xl-3">
          <div class="metric-card-shell">
            <div class="metric-card-core">
              <div class="metric-card-body">
                <div class="metric-details">
                  <span class="metric-label font-serif">Thành viên</span>
                  <h3 class="metric-value">{{ stats.totalUsers }}</h3>
                </div>
                <div class="metric-icon-box metric-icon-success">
                  <icon icon="fa-solid fa-users" />
                </div>
              </div>
              <div class="metric-footer">
                <span class="metric-trend text-success"><icon icon="fa-solid fa-arrow-trend-up" /> +5%</span>
                <span class="metric-trend-label">đăng ký mới tuần này</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Active Sessions Metric -->
        <div class="col-12 col-sm-6 col-xl-3">
          <div class="metric-card-shell">
            <div class="metric-card-core">
              <div class="metric-card-body">
                <div class="metric-details">
                  <span class="metric-label font-serif">Đang mượn</span>
                  <h3 class="metric-value">{{ stats.activeLoans }}</h3>
                </div>
                <div class="metric-icon-box metric-icon-warning">
                  <icon icon="fa-solid fa-book-open-reader" />
                </div>
              </div>
              <div class="metric-footer">
                <span class="metric-trend text-danger"><icon icon="fa-solid fa-arrow-trend-down" /> -2%</span>
                <span class="metric-trend-label">so với tuần trước</span>
              </div>
            </div>
          </div>
        </div>

        <!-- New Reviews Metric -->
        <div class="col-12 col-sm-6 col-xl-3">
          <div class="metric-card-shell">
            <div class="metric-card-core">
              <div class="metric-card-body">
                <div class="metric-details">
                  <span class="metric-label font-serif">Đánh giá mới</span>
                  <h3 class="metric-value">{{ stats.newReviews }}</h3>
                </div>
                <div class="metric-icon-box metric-icon-danger">
                  <icon icon="fa-solid fa-star" />
                </div>
              </div>
              <div class="metric-footer">
                <span class="metric-trend text-success"><icon icon="fa-solid fa-arrow-trend-up" /> +28%</span>
                <span class="metric-trend-label">đánh giá mới hôm nay</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Main Workspace Split Panels -->
      <div class="row g-4">
        <!-- Recent Activities Table Panel -->
        <div class="col-xl-8 col-lg-12">
          <div class="dashboard-panel shadow-sm">
            <div class="panel-header border-bottom">
              <div class="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center w-100 gap-3">
                <div class="panel-title-group">
                  <h3 class="panel-title font-serif">Hoạt động hệ thống gần đây</h3>
                  <span class="panel-subtitle">Nhật ký tác vụ trong ngày</span>
                </div>
                <!-- Controls: Filter & Search -->
                <div class="panel-actions-wrapper d-flex gap-2 w-100 w-sm-auto">
                  <div class="search-box-panel">
                    <input 
                      v-model="searchQuery" 
                      type="text" 
                      placeholder="Tìm nhật ký..." 
                      class="panel-search-input"
                    />
                    <icon icon="fa-solid fa-magnifying-glass" class="panel-search-icon" />
                  </div>
                  <select v-model="statusFilter" class="panel-filter-select">
                    <option value="ALL">Tất cả trạng thái</option>
                    <option value="SUCCESS">Thành công</option>
                    <option value="WARNING">Cảnh báo</option>
                  </select>
                </div>
              </div>
            </div>
            
            <div class="panel-body p-0">
              <div class="table-responsive">
                <table class="table custom-dashboard-table align-middle">
                  <thead>
                    <tr>
                      <th @click="toggleSort" class="sortable-header">
                        Quản trị viên / Người dùng 
                        <icon :icon="sortOrder === 'asc' ? 'fa-solid fa-arrow-up-short-wide' : 'fa-solid fa-arrow-down-wide-short'" class="ms-1" />
                      </th>
                      <th>Hành động</th>
                      <th>Đối tượng tác vụ</th>
                      <th>Thời gian</th>
                      <th>Trạng thái</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="log in filteredLogs" :key="log.id" class="table-row-hover">
                      <td>
                        <div class="d-flex align-items-center gap-2">
                          <div class="table-user-avatar">
                            <icon icon="fa-solid fa-circle-user" />
                          </div>
                          <span class="fw-semibold text-primary-color">{{ log.actor }}</span>
                        </div>
                      </td>
                      <td>{{ log.action }}</td>
                      <td>
                        <span class="d-inline-block text-truncate text-muted-dark fw-medium" style="max-width: 160px; vertical-align: bottom;">
                          {{ log.target }}
                        </span>
                      </td>
                      <td class="text-muted fs-8">{{ log.timestamp }}</td>
                      <td>
                        <span 
                          class="badge-pill" 
                          :class="log.status === 'Thành công' ? 'badge-pill-success' : 'badge-pill-warning'"
                        >
                          {{ log.status }}
                        </span>
                      </td>
                    </tr>
                    <tr v-if="filteredLogs.length === 0">
                      <td colspan="5" class="text-center py-5 text-muted font-serif">
                        <icon icon="fa-solid fa-inbox" class="fs-2 mb-2 d-block text-muted-dark" />
                        Không tìm thấy hoạt động nào phù hợp.
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <!-- System Console Actions Panel (tactile button-in-button CTA structure) -->
        <div class="col-xl-4 col-lg-12">
          <div class="dashboard-panel shadow-sm">
            <div class="panel-header border-bottom">
              <h3 class="panel-title font-serif">Bàn điều khiển hệ thống</h3>
              <span class="panel-subtitle">Tác vụ khẩn cấp & tối ưu</span>
            </div>
            
            <div class="panel-body">
              <div class="console-actions-list d-flex flex-column gap-3">
                <!-- Action 1: Database backup -->
                <button 
                  @click="runDatabaseBackup" 
                  class="btn-console-action"
                  :disabled="actionsLoading.backup"
                >
                  <span class="action-btn-left">
                    <span v-if="actionsLoading.backup" class="spinner-border spinner-border-sm me-2" role="status"></span>
                    <icon v-else icon="fa-solid fa-database" class="action-icon" />
                    <span>{{ actionsLoading.backup ? 'Đang sao lưu dữ liệu...' : 'Sao lưu Cơ sở dữ liệu' }}</span>
                  </span>
                  <span class="action-btn-right-pill">
                    <icon icon="fa-solid fa-chevron-right" />
                  </span>
                </button>

                <!-- Action 2: System Clean -->
                <button 
                  @click="runSystemClean" 
                  class="btn-console-action-warning"
                  :disabled="actionsLoading.clean"
                >
                  <span class="action-btn-left">
                    <span v-if="actionsLoading.clean" class="spinner-border spinner-border-sm me-2" role="status"></span>
                    <icon v-else icon="fa-solid fa-broom" class="action-icon" />
                    <span>{{ actionsLoading.clean ? 'Đang dọn dẹp...' : 'Dọn dẹp tệp tin rác' }}</span>
                  </span>
                  <span class="action-btn-right-pill">
                    <icon icon="fa-solid fa-chevron-right" />
                  </span>
                </button>

                <!-- Action 3: Dispatch general notice -->
                <button 
                  @click="showAnnouncementPrompt" 
                  class="btn-console-action-notice"
                >
                  <span class="action-btn-left">
                    <icon icon="fa-solid fa-bullhorn" class="action-icon" />
                    <span>Gửi thông báo thành viên</span>
                  </span>
                  <span class="action-btn-right-pill">
                    <icon icon="fa-solid fa-paper-plane" />
                  </span>
                </button>
              </div>

              <!-- Health Status card -->
              <div class="system-health-box mt-4 p-3 rounded bg-light border">
                <h4 class="h6 font-serif fw-bold text-primary mb-3">Tình trạng máy chủ</h4>
                <div class="health-item mb-2 d-flex justify-content-between fs-8-5">
                  <span class="text-muted">Bộ nhớ đệm (Cache RAM):</span>
                  <span class="text-success fw-bold">Ổn định (820MB)</span>
                </div>
                <div class="health-item mb-2 d-flex justify-content-between fs-8-5">
                  <span class="text-muted">CPU Load:</span>
                  <span class="text-dark fw-bold">12% (4 Cores online)</span>
                </div>
                <div class="health-item d-flex justify-content-between fs-8-5">
                  <span class="text-muted">Cập nhật ứng dụng:</span>
                  <span class="text-primary fw-bold">v1.2.0-stable</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ==============================================
         TAB 2: BÁO CÁO HIỆU SUẤT (STATISTICS VISUALS)
         ============================================== -->
    <div v-if="activeTab === 'analytics'" class="tab-pane-content fade-in">
      <div class="row g-4">
        <!-- Loan performance charts mock (SVG vector drawings) -->
        <div class="col-lg-8 col-md-12">
          <div class="dashboard-panel shadow-sm">
            <div class="panel-header border-bottom">
              <h3 class="panel-title font-serif">Biểu đồ lượt mượn sách hàng tuần</h3>
              <span class="panel-subtitle">Lượt mượn sách thực tế trong 7 ngày gần nhất</span>
            </div>
            <div class="panel-body py-4">
              <!-- SVG Visual Graph Chart -->
              <div class="svg-chart-container position-relative">
                <svg viewBox="0 0 600 240" class="w-100 h-auto">
                  <!-- Grid lines -->
                  <line x1="50" y1="40" x2="550" y2="40" stroke="#f1efe9" stroke-width="1" />
                  <line x1="50" y1="90" x2="550" y2="90" stroke="#f1efe9" stroke-width="1" />
                  <line x1="50" y1="140" x2="550" y2="140" stroke="#f1efe9" stroke-width="1" />
                  <line x1="50" y1="190" x2="550" y2="190" stroke="#f1efe9" stroke-dasharray="4" stroke-width="1" />
                  
                  <!-- Y-Axis labels -->
                  <text x="30" y="45" font-size="10" fill="#78756f" text-anchor="middle">100</text>
                  <text x="30" y="95" font-size="10" fill="#78756f" text-anchor="middle">50</text>
                  <text x="30" y="145" font-size="10" fill="#78756f" text-anchor="middle">20</text>
                  <text x="30" y="195" font-size="10" fill="#78756f" text-anchor="middle">0</text>
                  
                  <!-- Area path under the curve -->
                  <path d="M 50 190 Q 130 110, 210 130 T 370 80 T 530 50 L 530 190 Z" fill="rgba(30, 62, 63, 0.05)" />
                  
                  <!-- Main Curve Path -->
                  <path d="M 50 190 Q 130 110, 210 130 T 370 80 T 530 50" fill="none" stroke="var(--primary)" stroke-width="3" stroke-linecap="round" />
                  
                  <!-- Data Dots & Tooltips -->
                  <g class="chart-point-group">
                    <circle cx="130" cy="110" r="5" fill="#ca8a04" stroke="#ffffff" stroke-width="1.5" />
                    <text x="130" y="95" font-size="9" font-weight="bold" fill="var(--primary)" text-anchor="middle">38 lượt</text>
                    
                    <circle cx="210" cy="130" r="5" fill="#ca8a04" stroke="#ffffff" stroke-width="1.5" />
                    <text x="210" y="115" font-size="9" font-weight="bold" fill="var(--primary)" text-anchor="middle">26 lượt</text>
                    
                    <circle cx="370" cy="80" r="5" fill="#ca8a04" stroke="#ffffff" stroke-width="1.5" />
                    <text x="370" y="65" font-size="9" font-weight="bold" fill="var(--primary)" text-anchor="middle">62 lượt</text>
                    
                    <circle cx="530" cy="50" r="5" fill="#ca8a04" stroke="#ffffff" stroke-width="1.5" />
                    <text x="530" y="35" font-size="9" font-weight="bold" fill="var(--primary)" text-anchor="middle">84 lượt</text>
                  </g>
                  
                  <!-- X-Axis Labels -->
                  <text x="50" y="215" font-size="10" fill="#78756f" text-anchor="middle">Thứ 2</text>
                  <text x="130" y="215" font-size="10" fill="#78756f" text-anchor="middle">Thứ 3</text>
                  <text x="210" y="215" font-size="10" fill="#78756f" text-anchor="middle">Thứ 4</text>
                  <text x="290" y="215" font-size="10" fill="#78756f" text-anchor="middle">Thứ 5</text>
                  <text x="370" y="215" font-size="10" fill="#78756f" text-anchor="middle">Thứ 6</text>
                  <text x="450" y="215" font-size="10" fill="#78756f" text-anchor="middle">Thứ 7</text>
                  <text x="530" y="215" font-size="10" fill="#78756f" text-anchor="middle">Chủ Nhật</text>
                </svg>
              </div>
            </div>
            <div class="panel-footer bg-light border-top p-3 text-start fs-8 text-muted">
              <icon icon="fa-solid fa-circle-info" class="me-1" /> Dữ liệu được đồng bộ hóa tự động từ máy chủ API mỗi 10 phút.
            </div>
          </div>
        </div>

        <!-- Book Category distribution panel (SVG Radial progress indicator) -->
        <div class="col-lg-4 col-md-12">
          <div class="dashboard-panel shadow-sm">
            <div class="panel-header border-bottom">
              <h3 class="panel-title font-serif">Cơ cấu thể loại sách</h3>
              <span class="panel-subtitle">Tỷ lệ danh mục sách trong kho</span>
            </div>
            <div class="panel-body">
              <div class="radial-charts-wrapper d-flex justify-content-center py-3">
                <!-- SVG circular donut structure -->
                <svg width="180" height="180" viewBox="0 0 100 100" class="donut-chart">
                  <circle cx="50" cy="50" r="40" fill="transparent" stroke="#f1efe9" stroke-width="10" />
                  <!-- Literature Category segment (50%) -->
                  <circle cx="50" cy="50" r="40" fill="transparent" stroke="var(--primary)" stroke-width="10" 
                          stroke-dasharray="125.6 251.2" stroke-dashoffset="0" />
                  <!-- Science Category segment (30%) -->
                  <circle cx="50" cy="50" r="40" fill="transparent" stroke="#ca8a04" stroke-width="10" 
                          stroke-dasharray="75.3 251.2" stroke-dashoffset="-125.6" />
                  <!-- Others Category segment (20%) -->
                  <circle cx="50" cy="50" r="40" fill="transparent" stroke="#a84a32" stroke-width="10" 
                          stroke-dasharray="50.2 251.2" stroke-dashoffset="-200.9" />
                  <!-- Center Hole Text -->
                  <text x="50" y="47" font-size="10" font-weight="bold" fill="var(--primary)" text-anchor="middle">Thư viện</text>
                  <text x="50" y="58" font-size="7" fill="var(--text-muted)" text-anchor="middle">1.2K cuốn</text>
                </svg>
              </div>

              <!-- Legends of chart categories -->
              <div class="category-legends mt-3">
                <div class="legend-item mb-2 d-flex align-items-center justify-content-between fs-8">
                  <div class="d-flex align-items-center gap-2">
                    <span class="legend-dot color-primary"></span>
                    <span class="fw-semibold">Văn học nghệ thuật (50%)</span>
                  </div>
                  <span class="text-muted-dark">620 cuốn</span>
                </div>
                <div class="legend-item mb-2 d-flex align-items-center justify-content-between fs-8">
                  <div class="d-flex align-items-center gap-2">
                    <span class="legend-dot color-accent"></span>
                    <span class="fw-semibold">Khoa học & Đời sống (30%)</span>
                  </div>
                  <span class="text-muted-dark">372 cuốn</span>
                </div>
                <div class="legend-item d-flex align-items-center justify-content-between fs-8">
                  <div class="d-flex align-items-center gap-2">
                    <span class="legend-dot color-danger"></span>
                    <span class="fw-semibold">Thể loại khác (20%)</span>
                  </div>
                  <span class="text-muted-dark">248 cuốn</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ==============================================
         TAB 3: LỊCH TRÌNH & HỆ THỐNG
         ============================================== -->
    <div v-if="activeTab === 'schedule'" class="tab-pane-content fade-in">
      <div class="dashboard-panel shadow-sm">
        <div class="panel-header border-bottom">
          <h3 class="panel-title font-serif">Lịch trình Sao lưu & Bảo trì tự động</h3>
          <span class="panel-subtitle">Hệ thống thực thi nền định kỳ</span>
        </div>
        <div class="panel-body">
          <div class="timeline-list">
            <!-- Timeline Item 1 -->
            <div class="timeline-item-shell d-flex gap-3 mb-4">
              <div class="timeline-marker">
                <icon icon="fa-solid fa-clock" class="marker-icon-active" />
                <span class="marker-line"></span>
              </div>
              <div class="timeline-content p-3 rounded border">
                <div class="d-flex justify-content-between align-items-start mb-2">
                  <h4 class="h6 font-serif fw-bold text-primary m-0">Sao lưu cơ sở dữ liệu hàng đêm</h4>
                  <span class="badge bg-success">Auto Active</span>
                </div>
                <p class="fs-8 text-muted mb-0">Hệ thống tự động nén dữ liệu SQL và tải lên Cloud Storage bảo mật lúc <strong>02:00 sáng hàng ngày</strong>.</p>
              </div>
            </div>

            <!-- Timeline Item 2 -->
            <div class="timeline-item-shell d-flex gap-3 mb-4">
              <div class="timeline-marker">
                <icon icon="fa-solid fa-rotate" class="marker-icon" />
                <span class="marker-line"></span>
              </div>
              <div class="timeline-content p-3 rounded border">
                <div class="d-flex justify-content-between align-items-start mb-2">
                  <h4 class="h6 font-serif fw-bold text-primary m-0">Dọn dẹp Cache & Files dư thừa</h4>
                  <span class="badge bg-secondary">Weekly</span>
                </div>
                <p class="fs-8 text-muted mb-0">Tự động tối ưu hóa bộ nhớ tạm, giải phóng dung lượng đĩa ảo vào mỗi <strong>Chủ Nhật lúc 04:00 sáng</strong>.</p>
              </div>
            </div>

            <!-- Timeline Item 3 -->
            <div class="timeline-item-shell d-flex gap-3">
              <div class="timeline-marker">
                <icon icon="fa-solid fa-shield-halved" class="marker-icon" />
              </div>
              <div class="timeline-content p-3 rounded border bg-light">
                <div class="d-flex justify-content-between align-items-start mb-2">
                  <h4 class="h6 font-serif fw-bold text-muted m-0">Kiểm tra bảo mật toàn diện</h4>
                  <span class="badge bg-danger">Manual Trigger Only</span>
                </div>
                <p class="fs-8 text-muted mb-0">Quét bảo mật các cổng kết nối API, rà soát lỗ hổng thiết bị truy cập bất thường.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Announcement Modal Popup Dialog -->
    <teleport to="body">
      <div v-if="announcementModalVisible" class="modal-backdrop-custom d-flex align-items-center justify-content-center">
        <div class="modal-card-custom shadow-lg fade-in">
          <div class="modal-header-custom border-bottom">
            <h3 class="modal-title-custom font-serif text-primary-color"><icon icon="fa-solid fa-bullhorn" class="me-2" />Thông báo thành viên</h3>
            <button @click="closeAnnouncementModal" class="btn-close-modal"><icon icon="fa-solid fa-xmark" /></button>
          </div>
          <div class="modal-body-custom py-3 text-start">
            <div class="custom-form-group mb-3">
              <label class="form-label font-serif">Tiêu đề thông báo</label>
              <input v-model="announcementTitle" type="text" class="custom-input-modal" placeholder="Nhập tiêu đề..." />
            </div>
            <div class="custom-form-group">
              <label class="form-label font-serif">Nội dung thông báo chi tiết</label>
              <textarea v-model="announcementBody" class="custom-textarea-modal" placeholder="Nhập nội dung..." rows="4"></textarea>
            </div>
          </div>
          <div class="modal-footer-custom border-top pt-3 d-flex justify-content-end gap-2">
            <button @click="closeAnnouncementModal" class="btn btn-outline-secondary px-3 py-2 rounded-pill font-serif">Hủy bỏ</button>
            <button @click="sendAnnouncement" class="btn btn-primary px-4 py-2 rounded-pill font-serif" :disabled="!announcementTitle || !announcementBody">Gửi đi</button>
          </div>
        </div>
      </div>
    </teleport>
  </div>
</template>

<script src="./dashboard.js"></script>
<style scoped src="./dashboard.css"></style>
